/**
 * Centralni SEO rečnik.
 *
 * Jedini izvor istine za naslove, opise, H1 i tekstualni sadržaj listing
 * stranica. Namerno je običan ESM `.js` (a ne `.ts`) da bi ga mogli uvesti
 * i aplikacija (preko Vite-a) i build-time prerender skripta (preko Node-a),
 * bez dupliranja teksta. Tipovi žive u `seoTaxonomy.d.ts`.
 *
 * Kad dodate novi grad — dopunite CITY_FORMS. Kad dodate kategoriju —
 * dopunite CATEGORY_SEO i CATEGORY_KEYS.
 */

/**
 * Namerno tvrdo upisan, a ne iz `VITE_APP_BASE_URL`.
 *
 * Odavde se grade canonical linkovi i sitemap, a oni uvek moraju da pokazuju
 * na produkcijski domen. Da se vuče iz env-a, lokalni build bi upisao
 * `http://localhost:3001` u canonical svake od 34 stranice.
 */
export const SITE_URL = "https://rasporedime.rs";

export const CATEGORY_KEYS = [
  "hair",
  "makeup",
  "nails",
  "eyelashes",
  "eyebrows",
  "waxing",
  "massage",
  "physiotherapy",
  "stomatology",
  "antiaging",
];

/**
 * Gradovi u kojima platforma radi. `locative` je oblik za "u ___" jer
 * "u gradu Pirot" zvuči kao mašinski prevod, a ljudi kucaju "šminker Pirot".
 */
export const CITY_FORMS = {
  pirot: { slug: "pirot", name: "Pirot", locative: "u Pirotu", nameEn: "Pirot" },
  nis: { slug: "nis", name: "Niš", locative: "u Nišu", nameEn: "Nis" },
};

export const CITIES = Object.values(CITY_FORMS);

const capitalize = (value) => value.charAt(0).toUpperCase() + value.slice(1);

export const CITY_SLUGS = Object.keys(CITY_FORMS);

/** Slug iz srpskog naziva — mora da se poklapa sa `toSlug` iz @models. */
const CHAR_MAP = {
  č: "c",
  ć: "c",
  š: "s",
  ž: "z",
  đ: "dj",
  Č: "C",
  Ć: "C",
  Š: "S",
  Ž: "Z",
  Đ: "Dj",
};

export const slugify = (value) =>
  String(value)
    .replace(/[čćšžđČĆŠŽĐ]/g, (ch) => CHAR_MAP[ch] ?? ch)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

/**
 * Oblici grada za zadati naziv. Za gradove koje ne znamo unapred (novi
 * gradovi stižu sa API-ja pre nego što ih upišemo ovde) pada na neutralno
 * "u gradu X" umesto da pogađa padež.
 */
export const cityForms = (nameSr) => {
  if (!nameSr) return null;
  const known = CITY_FORMS[slugify(nameSr)];
  if (known) return known;
  return {
    slug: slugify(nameSr),
    name: nameSr,
    locative: `u gradu ${nameSr}`,
    nameEn: nameSr,
  };
};

/* ── Kategorije ──────────────────────────────────────────────────────
 *
 * `synonyms` su termini koje ljudi zaista kucaju u Google, uključujući
 * varijante bez kvačica ("sminker") i pozajmljenice ("make up artist").
 * Ne služe `meta keywords` tagu (Google ga ignoriše od 2009) nego ulaze
 * u vidljiv tekst stranice — jedino mesto gde ih Google stvarno računa.
 */
export const CATEGORY_SEO = {
  hair: {
    label: "Frizeri",
    labelShort: "Frizura",
    headTerm: "frizer",
    place: "Frizerski saloni",
    seekNoun: "frizera",
    atNoun: "frizera",
    titleLead: "Frizer",
    titleExtra: "frizerski saloni, berber i šišanje",
    h1Extra: "frizeri, berberi i šišanje",
    services: [
      "šišanje",
      "farbanje kose",
      "pramenovi i balajaž",
      "feniranje i stilizovanje",
      "tretmani za kosu",
      "muško šišanje",
      "svečane frizure",
    ],
    synonyms: [
      "berber",
      "berberski salon",
      "frizer",
      "frizerka",
      "frizerski salon",
      "šišanje",
      "sisanje",
      "farbanje kose",
      "feniranje",
      "pramenovi",
      "balajaž",
      "muški frizer",
      "ženski frizer",
      "hair stylist",
      "berberin",
      "berbernica",
      "frizerske usluge",
    ],
    faqExtra: (c) => ({
      q: `Da li frizerski saloni ${c.locative} rade i muška šišanja?`,
      a: "Većina salona radi i muška i ženska šišanja. Tačan spisak usluga, cenu i trajanje svake usluge vidite na profilu salona pre nego što zakažete.",
    }),
  },

  makeup: {
    label: "Šminkeri",
    labelShort: "Šminkanje",
    headTerm: "šminkanje",
    place: "Make up studiji",
    seekNoun: "šminkera",
    atNoun: "šminkera",
    titleLead: "Šminkanje",
    titleExtra: "šminker i make up artist",
    h1Extra: "šminkeri i make up artisti",
    services: [
      "šminka za venčanje",
      "šminka za maturu",
      "svečana šminka",
      "dnevna šminka",
      "proba šminke",
      "šminkanje za slavu i proslave",
    ],
    synonyms: [
      "šminker",
      "šminkerka",
      "sminker",
      "sminkerka",
      "šminka",
      "sminka",
      "make up",
      "makeup",
      "make up artist",
      "makeup artist",
      "vizažista",
      "šminkanje",
      "sminkanje",
      "šminka za venčanje",
      "svadbena šminka",
      "šminka za maturu",
      "profesionalna šminka",
      "make up studio",
    ],
    faqExtra: (c) => ({
      q: `Da li šminkeri ${c.locative} rade šminku za venčanje?`,
      a: "Većina make up artista nudi svadbenu šminku, često i probu šminke pre samog dana. Usluge koje salon radi vidite na njegovom profilu, zajedno sa cenom i trajanjem.",
    }),
  },

  nails: {
    label: "Saloni za manikir",
    labelShort: "Manikir",
    headTerm: "manikir",
    place: "Nail studiji",
    seekNoun: "salon za manikir",
    atNoun: "salona za manikir",
    titleLead: "Manikir",
    titleExtra: "gel lak, nadogradnja noktiju i pedikir",
    h1Extra: "gel lak, pedikir i nadogradnja",
    services: [
      "manikir",
      "pedikir",
      "gel lak",
      "nadogradnja noktiju",
      "gradnja gelom",
      "korekcija noktiju",
      "nail art",
    ],
    synonyms: [
      "manikir",
      "pedikir",
      "nokti",
      "nadogradnja noktiju",
      "gel nokti",
      "gel lak",
      "gradnja gelom",
      "akrilni nokti",
      "salon za nokte",
      "nail art",
      "nail studio",
      "nail artist",
      "trajni lak",
      "korekcija noktiju",
    ],
    faqExtra: (c) => ({
      q: `Koliko traje nadogradnja noktiju ${c.locative}?`,
      a: "Zavisi od tehnike i salona — obično između sat i dva. Trajanje svake usluge je navedeno na profilu salona, pa termin birate tako da vam se uklopi u dan.",
    }),
  },

  eyelashes: {
    label: "Saloni za trepavice",
    labelShort: "Trepavice",
    headTerm: "trepavice",
    place: "Lash studiji",
    seekNoun: "salon za trepavice",
    atNoun: "salona za trepavice",
    titleLead: "Trepavice",
    titleExtra: "nadogradnja i laminacija trepavica",
    h1Extra: "nadogradnja i laminacija trepavica",
    services: [
      "nadogradnja trepavica",
      "klasične trepavice",
      "volumen trepavice",
      "ruske trepavice",
      "laminacija trepavica",
      "popravka i korekcija",
    ],
    synonyms: [
      "nadogradnja trepavica",
      "trepavice",
      "ruske trepavice",
      "volumen trepavice",
      "klasične trepavice",
      "laminacija trepavica",
      "lash lift",
      "lash artist",
      "produžavanje trepavica",
      "trepavice cena",
    ],
    faqExtra: () => ({
      q: "Koliko dugo traju nadograđene trepavice?",
      a: "Uobičajeno tri do četiri nedelje, uz korekciju po potrebi. Saloni koji rade korekcije to navode među uslugama na svom profilu.",
    }),
  },

  eyebrows: {
    label: "Saloni za obrve",
    labelShort: "Obrve",
    headTerm: "obrve",
    place: "Brow studiji",
    seekNoun: "salon za obrve",
    atNoun: "salona za obrve",
    titleLead: "Obrve",
    titleExtra: "oblikovanje, laminacija i microblading",
    h1Extra: "oblikovanje, laminacija i microblading",
    services: [
      "oblikovanje obrva",
      "laminacija obrva",
      "microblading",
      "powder brows",
      "henna obrve",
      "bojenje obrva",
    ],
    synonyms: [
      "obrve",
      "oblikovanje obrva",
      "laminacija obrva",
      "microblading",
      "mikroblejding",
      "powder brows",
      "puder obrve",
      "henna obrve",
      "brow artist",
      "trajna šminka obrva",
      "bojenje obrva",
      "regulacija obrva",
    ],
    faqExtra: () => ({
      q: "Koja je razlika između laminacije obrva i microbladinga?",
      a: "Laminacija je privremeno oblikovanje sopstvenih dlačica i traje nekoliko nedelja, dok je microblading polutrajna tehnika crtanja dlačica pigmentom. Salon na svom profilu navodi koje tehnike radi.",
    }),
  },

  waxing: {
    label: "Saloni za depilaciju",
    labelShort: "Depilacija",
    headTerm: "depilacija",
    place: "Saloni za depilaciju",
    seekNoun: "salon za depilaciju",
    atNoun: "salona za depilaciju",
    titleLead: "Depilacija",
    titleExtra: "depilacija voskom i šećernom pastom",
    h1Extra: "vosak, sugaring i laser",
    services: [
      "depilacija voskom",
      "sugaring",
      "brazilska depilacija",
      "depilacija nogu",
      "depilacija ruku",
      "depilacija lica",
    ],
    synonyms: [
      "depilacija",
      "depilacija voskom",
      "waxing",
      "sugaring",
      "šećerna pasta",
      "brazilska depilacija",
      "lasersko uklanjanje dlačica",
      "laser depilacija",
      "epilacija",
      "depilacija nogu",
      "depilacija cena",
    ],
    faqExtra: (c) => ({
      q: `Da li saloni ${c.locative} rade depilaciju šećernom pastom?`,
      a: "Neki rade vosak, neki sugaring, a neki i jedno i drugo. Tehniku koju salon koristi vidite u spisku usluga na njegovom profilu.",
    }),
  },

  massage: {
    label: "Saloni za masažu",
    labelShort: "Masaža",
    headTerm: "masaža",
    place: "Saloni za masažu",
    seekNoun: "masera",
    atNoun: "masera",
    titleLead: "Masaža",
    titleExtra: "relax, sportska i anticelulit masaža",
    h1Extra: "relax, sportska i anticelulit masaža",
    services: [
      "relax masaža",
      "sportska masaža",
      "anticelulit masaža",
      "masaža leđa",
      "terapeutska masaža",
      "limfna drenaža",
    ],
    synonyms: [
      "masaža",
      "masaza",
      "maser",
      "maserka",
      "relax masaža",
      "sportska masaža",
      "anticelulit masaža",
      "terapeutska masaža",
      "limfna drenaža",
      "masaža leđa",
      "salon za masažu",
      "masaža celog tela",
      "masaža cena",
    ],
    faqExtra: () => ({
      q: "Koja masaža pomaže kod bolova u leđima?",
      a: "Najčešće terapeutska ili sportska masaža, a kod dužih tegoba i fizioterapija. Ako niste sigurni, pozovite salon — kontakt telefon stoji na profilu.",
    }),
  },

  physiotherapy: {
    label: "Fizioterapeuti",
    labelShort: "Fizioterapija",
    headTerm: "fizioterapija",
    place: "Fizioterapeutske ordinacije",
    seekNoun: "fizioterapeuta",
    atNoun: "fizioterapeuta",
    titleLead: "Fizioterapija",
    titleExtra: "fizikalna terapija i kiropraktika",
    h1Extra: "fizioterapeuti i fizikalna terapija",
    services: [
      "fizikalna terapija",
      "kineziterapija",
      "manuelna terapija",
      "rehabilitacija posle povrede",
      "terapija bola u leđima",
      "sportske povrede",
    ],
    synonyms: [
      "fizioterapija",
      "fizioterapeut",
      "fizikalna terapija",
      "kineziterapija",
      "rehabilitacija",
      "manuelna terapija",
      "kiropraktika",
      "kiropraktičar",
      "hiropraktika",
      "terapija bola",
      "bol u leđima",
      "išijas",
      "sportske povrede",
      "fizijatar",
    ],
    faqExtra: (c) => ({
      q: `Da li mi je potreban uput za fizioterapiju ${c.locative}?`,
      a: "Za privatne ordinacije uput najčešće nije potreban. Ako imate nalaz lekara, ponesite ga na prvi pregled — terapeutu pomaže da odredi plan terapije.",
    }),
  },

  stomatology: {
    label: "Stomatolozi",
    labelShort: "Stomatologija",
    headTerm: "zubar",
    place: "Stomatološke ordinacije",
    seekNoun: "stomatologa",
    atNoun: "stomatologa",
    titleLead: "Zubar i stomatolog",
    titleExtra: "stomatološke ordinacije",
    h1Extra: "ordinacije i zubarske usluge",
    services: [
      "popravka zuba",
      "vađenje zuba",
      "uklanjanje kamenca",
      "izbeljivanje zuba",
      "protetika i krunice",
      "implanti",
      "ortodoncija",
    ],
    synonyms: [
      "stomatolog",
      "zubar",
      "zubarska ordinacija",
      "stomatološka ordinacija",
      "popravka zuba",
      "vađenje zuba",
      "izbeljivanje zuba",
      "protetika",
      "implanti",
      "ortodoncija",
      "proteza",
      "zubna higijena",
      "uklanjanje kamenca",
      "plombiranje",
    ],
    faqExtra: (c) => ({
      q: `Mogu li da zakažem hitan pregled kod zubara ${c.locative}?`,
      a: "Za hitne slučajeve najbrže je da pozovete ordinaciju direktno — broj telefona stoji na profilu. Za redovne preglede zakazivanje preko sajta radi non-stop.",
    }),
  },

  antiaging: {
    label: "Kozmetičari",
    labelShort: "Nega lica",
    headTerm: "kozmetičar",
    place: "Kozmetički saloni",
    seekNoun: "kozmetički salon",
    atNoun: "kozmetičara",
    titleLead: "Kozmetičar",
    titleExtra: "nega lica, čišćenje lica i anti-age",
    h1Extra: "nega lica i anti-age tretmani",
    services: [
      "nega lica",
      "čišćenje lica",
      "hijaluronski fileri",
      "mezoterapija",
      "hemijski piling",
      "dermapen i mikroneedling",
      "tretmani protiv bora",
    ],
    synonyms: [
      "kozmetičar",
      "kozmetičarka",
      "kozmetički salon",
      "kozmetički tretmani",
      "tretman lica",
      "anti age",
      "anti-age tretmani",
      "pomlađivanje lica",
      "tretmani protiv bora",
      "hijaluron",
      "hijaluronski filer",
      "mezoterapija",
      "botoks",
      "filer",
      "nega lica",
      "čišćenje lica",
      "hemijski piling",
      "dermapen",
      "mikroneedling",
      "estetski tretmani",
    ],
    faqExtra: () => ({
      q: "Koji anti-age tretman je pravi za mene?",
      a: "Zavisi od stanja kože i onoga što želite da postignete. Većina salona radi konsultaciju pre tretmana — zakažite je kao i svaku drugu uslugu.",
    }),
  },
};

/* ── Sastavljanje teksta ─────────────────────────────────────────── */

const suffix = (city) => (city ? ` ${city.name}` : "");
const inPlace = (city) => (city ? ` ${city.locative}` : " u Srbiji");
const listAnd = (items) =>
  items.length < 2
    ? items.join("")
    : `${items.slice(0, -1).join(", ")} i ${items[items.length - 1]}`;

const NO_CITY = { name: "", locative: "u vašem gradu" };

/** "u Pirotu i Nišu" — lokativ svih gradova bez ponovljenog predloga. */
export const CITIES_IN = `u ${CITIES.map((c) => c.locative.replace(/^u /, "")).join(" i ")}`;

/**
 * SEO paket za listing stranicu (`/tenants/:category` i
 * `/city/:citySlug/:category`). `city` je rezultat `cityForms()` ili null.
 */
export const getListingSeo = (categoryKey, city) => {
  const cat = CATEGORY_SEO[categoryKey];
  if (!cat) return null;

  const where = inPlace(city);
  const path = city
    ? `/city/${city.slug}/${categoryKey}`
    : `/tenants/${categoryKey}`;

  // H1 se prikazuje u dva dela (glavni deo krupno, dopuna sitnije), ali je
  // tekst identičan i u prerenderu i u aplikaciji.
  const h1Main = city ? `${cat.titleLead} ${city.name}` : cat.titleLead;
  const h1Sub = cat.h1Extra;
  const h1 = `${h1Main} – ${h1Sub}`;

  const title = city
    ? `${cat.titleLead} ${city.name} – ${cat.titleExtra} | Rasporedi Me`
    : `${cat.titleLead} – ${cat.titleExtra} | Rasporedi Me`;

  // Zdravstvene kategorije se ne zovu "saloni" — inače ispadne "uporedite
  // salone" na stranici stomatologa.
  const isClinic = cat.place.includes("ordinacije");
  const unitPlural = isClinic ? "ordinacije" : "salone";
  const eachUnit = isClinic ? "svaku ordinaciju" : "svaki salon";

  // Konkretne usluge u opisu su ono što razlikuje stranice u rezultatima pretrage
  // (isti opšti šablon za svaku kategoriju bi svi izgledali isto).
  const buildDescription = (count) =>
    `Tražite ${cat.seekNoun}${where}? ${capitalize(cat.services.slice(0, count).join(", "))}. Uporedite ${unitPlural}, cene i ocene klijenata i zakažite termin online, 24/7.`;
  // Tri usluge kad staju u ~160 znakova koje Google prikazuje, inače dve.
  const description =
    buildDescription(3).length <= 160 ? buildDescription(3) : buildDescription(2);

  const intro = `${cat.place}${where} na jednom mestu. Za ${eachUnit} vidite spisak usluga sa cenama i trajanjem, ocene klijenata i slobodne termine — pa zakazujete odmah, bez čekanja odgovora na poruku.`;

  const servicesLine = `Najtraženije usluge: ${listAnd(cat.services)}.`;

  const relatedLine = `Ovo se traži i kao: ${cat.synonyms
    .slice(0, 10)
    .map((s) => (city ? `${s} ${city.name}` : s))
    .join(", ")}.`;

  const faq = [
    {
      q: `Kako da zakažem termin${where}?`,
      a: "Izaberite salon sa liste, otvorite njegov profil, izaberite uslugu i slobodan termin i potvrdite zakazivanje. Potvrdu dobijate odmah — bez pozivanja i bez čekanja odgovora na poruku.",
    },
    {
      q: `Koliko košta termin kod ${cat.atNoun}${where}?`,
      a: "Cene se razlikuju od salona do salona. Svaki salon na Rasporedi Me ima svoj cenovnik, pa cenu svake usluge vidite na njegovom profilu pre nego što zakažete.",
    },
    cat.faqExtra(city ?? NO_CITY),
    {
      q: "Da li mogu da zakažem van radnog vremena?",
      a: "Da. Zakazivanje preko Rasporedi Me radi 24 sata dnevno — birate neki od slobodnih termina salona kad god vam odgovara, i vikendom i noću.",
    },
  ];

  const cityKeywords = city
    ? `, ${cat.labelShort.toLowerCase()} ${city.name}, ${cat.seekNoun} ${city.name}`
    : "";

  return {
    categoryKey,
    city: city ?? null,
    path,
    url: `${SITE_URL}${path}`,
    h1,
    h1Main,
    h1Sub,
    title,
    description,
    keywords: `${cat.synonyms.join(", ")}${cityKeywords}, zakazivanje termina online, Rasporedi Me`,
    intro,
    servicesLine,
    relatedLine,
    services: cat.services,
    synonyms: cat.synonyms,
    faq,
    label: cat.label,
    labelShort: cat.labelShort,
    place: cat.place,
  };
};

/** SEO paket za `/city/:citySlug`. */
/**
 * Kategorije čiji se glavni izrazi pominju u naslovima zbirnih stranica (grad,
 * kategorije). Ne mogu sve — naslov preko ~70 znakova Google skraćuje.
 */
const TITLE_TERM_KEYS = ["hair", "nails", "stomatology"];

/** Šest izraza za opise (Google prikazuje ~160 znakova), ostali se pokrivaju sa "i još". */
const DESCRIPTION_TERM_KEYS = [
  "hair",
  "nails",
  "makeup",
  "stomatology",
  "antiaging",
  "physiotherapy",
];

const headTerms = (keys) => keys.map((k) => CATEGORY_SEO[k].headTerm);

export const getCityLandingSeo = (city) => {
  const cats = CATEGORY_KEYS.map((k) =>
    CATEGORY_SEO[k].labelShort.toLowerCase(),
  );
  const lower = city.name.toLowerCase();

  return {
    path: `/city/${city.slug}`,
    url: `${SITE_URL}/city/${city.slug}`,
    h1: `Zakazivanje termina ${city.name} – saloni i ordinacije`,
    // Naslov počinje onim što ljudi kucaju ("zakazivanje termina Pirot") i ima isti
    // početak kao H1, pa se pretraga i stranica slažu.
    title: `Zakazivanje termina ${city.name} – ${listAnd(headTerms(TITLE_TERM_KEYS))} | Rasporedi Me`,
    description: `Zakažite termin online ${city.locative}: ${headTerms(DESCRIPTION_TERM_KEYS).join(", ")} i još. Uporedite salone i ordinacije, cene i ocene klijenata — 24/7.`,
    keywords: `zakazivanje termina ${lower}, ${headTerms(CATEGORY_KEYS)
      .map((term) => `${term} ${lower}`)
      .join(", ")}, saloni ${lower}, Rasporedi Me`,
    intro: `Sve usluge koje se ${city.locative} mogu zakazati online, na jednom mestu: ${listAnd(cats)}. Izaberite kategoriju, uporedite salone po uslugama, cenama i ocenama, i zakažite termin bez poziva telefonom.`,
  };
};

/** SEO paket za `/categories`. */
export const getCategoriesLandingSeo = () => {
  const labels = CATEGORY_KEYS.map((k) => CATEGORY_SEO[k].label.toLowerCase());

  return {
    path: "/categories",
    url: `${SITE_URL}/categories`,
    h1: "Sve kategorije usluga",
    title: `Sve usluge – ${listAnd(headTerms(TITLE_TERM_KEYS))} | Rasporedi Me`,
    description: `Pregledajte sve usluge i zakažite termin online: ${headTerms(DESCRIPTION_TERM_KEYS).join(", ")} i još. Uporedite salone, cene i ocene klijenata.`,
    keywords: `kategorije usluga, ${headTerms(CATEGORY_KEYS).join(", ")}, zakazivanje termina online, Rasporedi Me`,
    intro: `Sve usluge koje se preko Rasporedi Me mogu zakazati online — ${listAnd(labels)} — ${CITIES_IN}.`,
  };
};

/** SEO paket za početnu (`/`). */
export const getHomeSeo = () => ({
  path: "/",
  url: `${SITE_URL}/`,
  h1: "Rasporedi Me – online zakazivanje termina",
  title: `Rasporedi Me – online zakazivanje termina | ${CITIES.map((c) => c.name).join(" i ")}`,
  description: `Zakažite termin online: ${headTerms(DESCRIPTION_TERM_KEYS).join(", ")} i još. Uporedite salone, cene i ocene klijenata. Trenutno ${CITIES_IN}.`,
  keywords: `zakazivanje termina online, ${CITIES.flatMap((c) =>
    headTerms(TITLE_TERM_KEYS).map((term) => `${term} ${c.name}`),
  ).join(", ")}, online rezervacija termina, Rasporedi Me`,
  intro: `Zakažite termin kod frizera, šminkera, kozmetičara ili zubara, u salonu za manikir, na masaži ili fizioterapiji — online, 24 sata dnevno. Trenutno ${CITIES_IN}.`,
});

/* ── Interni linkovi ─────────────────────────────────────────────── */

/** Kratka oznaka kategorije za link ("Šminka Niš"). */
export const categoryLinkLabel = (categoryKey, city) => {
  const lead = CATEGORY_SEO[categoryKey].titleLead;
  return city ? `${lead} ${city.name}` : lead;
};

/**
 * Susedni linkovi listing stranice — ostale kategorije u istom gradu i ista
 * kategorija u ostalim gradovima.
 *
 * Živi ovde jer isti graf grade i `scripts/prerender.mjs` (u sirovom HTML-u)
 * i `SeoContent.tsx` (posle hidracije); ako bi se razišli, crawler i korisnik
 * bi videli različite putanje kroz sajt.
 */
export const getListingLinks = (categoryKey, city) => ({
  siblings: CATEGORY_KEYS.filter((k) => k !== categoryKey).map((k) => ({
    href: city ? `/city/${city.slug}/${k}` : `/tenants/${k}`,
    text: categoryLinkLabel(k, city),
  })),
  otherCities: CITIES.filter((c) => c.slug !== city?.slug).map((c) => ({
    href: `/city/${c.slug}/${categoryKey}`,
    text: categoryLinkLabel(categoryKey, c),
  })),
});

/** Linkovi na gradskoj landing stranici. */
export const getCityLinks = (city) => ({
  categories: CATEGORY_KEYS.map((k) => ({
    href: `/city/${city.slug}/${k}`,
    text: categoryLinkLabel(k, city),
  })),
  otherCities: CITIES.filter((c) => c.slug !== city.slug).map((c) => ({
    href: `/city/${c.slug}`,
    text: c.name,
  })),
});
